import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { atom, useAtomValue } from 'jotai';
import { Layer, Popup, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'mapbox-gl';
import turfBbox from '@turf/bbox';
// Aliased: the bare name would shadow the global Map constructor below.
import { Map as PreviewMap } from 'components/map';
import { getMapDefaultConfig, mapClicksTakenAtom } from 'components/map/atoms';
import type { MapUserConfig } from 'components/map/types';
// Reuse the shared map popup styling (padding, min-width, font size).
import mapStyles from 'components/map/map.module.scss';
import {
  unionBounds,
  type BBox,
} from 'components/query-map/fit-bounds-control';
import { findCenter } from 'lib/map';
import { ACTIVE_COLOR, colorForArea } from './area-colors';
import style from './inventory-workspace-area-preview.module.scss';

// Everything off: this is a read-only preview inside a modal, so wells,
// development areas, basin/tier and title assets would only add noise, and
// scroll-zoom would steal the wheel from the modal body.
const baseMapConfig = getMapDefaultConfig();
const previewMapConfigAtom = atom<MapUserConfig>({
  ...baseMapConfig,
  wellsV2: { ...baseMapConfig.wellsV2, checked: false },
  // `checked: false` is not enough here. `Map` renders `DevelopmentAreasLayer`
  // unconditionally (map.tsx) — unlike `wellsV2`, which it guards on `checked` —
  // and the layer itself only tests the individual `options`. With
  // `horizontalUnits` defaulting to true, every unit in view still draws. The
  // options have to be cleared for the layer to render nothing.
  developmentAreas: {
    checked: false,
    options: {
      horizontalUnits: false,
      verticalUnits: false,
      fieldUnits: false,
      pricing: false,
    },
  },
  basinTier: { ...baseMapConfig.basinTier, checked: false },
  titleEntityAssets: { ...baseMapConfig.titleEntityAssets, checked: false },
  scrollZoom: { checked: false },
  popups: { checked: false },
});

// Every area is drawn with the same weight so no colour reads as more or less
// present than another; only the active row's fill lifts out of the set. (The
// mock additionally fades and dashes areas no included row uses, which made the
// paler colours near-invisible.)
const FILL_OPACITY_ACTIVE = 0.55;
const FILL_OPACITY_AREA = 0.14;
const STROKE_WIDTH = 2;

/** Zoom ceiling when framing the areas — a small union otherwise opens tight on one polygon. */
const AREA_MAX_ZOOM = 9.5;
/**
 * Zoom ceiling when a caller frames something smaller (the Eval TC Manager's slots). Past the LTD
 * tile's own detail zoom of 10, so the places draw as sticks rather than as one point each.
 */
const OVERRIDE_MAX_ZOOM = 14;

/** ~500 ft in degrees. Enough to give `cameraForBounds` room, small enough to keep the fit tight. */
const FRAMING_PAD_DEGREES = 0.0015;

/**
 * Grows a caller's box on both axes before it is framed.
 *
 * Two reasons, and the first is a correctness one: a single planned location is a LineString, and
 * parallel ones share a latitude span, so their extent can collapse to zero on an axis —
 * `cameraForBounds` answers a zoom in the twenties for that, landing the camera inside a well pad.
 * The second is plain framing: an exact extent puts the outermost stick on the canvas edge.
 *
 * @param {BBox} bounds - `[minLng, minLat, maxLng, maxLat]`.
 * @return {BBox} The same box with margin, guaranteed to have area.
 */
const padForFraming = ([minLng, minLat, maxLng, maxLat]: BBox): BBox => [
  minLng - FRAMING_PAD_DEGREES,
  minLat - FRAMING_PAD_DEGREES,
  maxLng + FRAMING_PAD_DEGREES,
  maxLat + FRAMING_PAD_DEGREES,
];

/**
 * Headless child of `<Map>` that keeps the GL canvas matched to its container.
 *
 * Mapbox measures the container once, at mount. Here that happens while the
 * modal is still settling, so the canvas keeps the dimensions it saw mid-layout:
 * the first frame is stale, and `fitBounds` computes its camera against the
 * wrong viewport, which leaves the preview framed on part of the set. Observing
 * the container covers the open animation and any later layout change.
 *
 * `resize()` never alters the container, so this cannot feed back into itself.
 *
 * @return {null} Always renders nothing.
 */
const KeepMapSized = () => {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
};

interface Camera {
  latitude: number;
  longitude: number;
  zoom: number;
}

/**
 * Headless child of `<Map>` that reports the camera framing `bounds`.
 *
 * `Map` drives its own camera: it jumps to the `latitude`/`longitude`/`zoom`
 * props in an effect gated on its map ref, which is populated after mount. That
 * jump therefore always lands last, and a `fitBounds` issued from a child is
 * overridden by it — the preview ends up centred on one area at the default
 * zoom. So rather than compete, this computes the camera that frames the bounds
 * and hands it back for the caller to pass down as those props, leaving `Map` to
 * do the moving.
 *
 * `cameraForBounds` is Mapbox's own solver, so it accounts for the real
 * container size; it is recomputed on resize because the canvas is still
 * settling while the modal opens.
 *
 * @param {object} props - Component props.
 * @param {BBox} props.bounds - The box to frame.
 * @param {number} props.padding - Inner padding, in pixels.
 * @param {number} props.maxZoom - Zoom ceiling for a small box.
 * @param {Function} props.onCamera - Receives the framing camera.
 * @return {null} Always renders nothing.
 */
const ReportFitCamera = ({
  bounds,
  padding,
  maxZoom,
  onCamera,
}: {
  bounds: BBox;
  padding: number;
  maxZoom: number;
  onCamera: (camera: Camera) => void;
}) => {
  const { current: map } = useMap();
  const signature = bounds.join(',');

  useEffect(() => {
    if (!map) return undefined;

    const report = () => {
      const camera = map.cameraForBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding, maxZoom }
      );
      if (!camera?.center || camera.zoom === undefined) return;
      const { lng, lat } = camera.center as { lng: number; lat: number };
      onCamera({ latitude: lat, longitude: lng, zoom: camera.zoom });
    };

    report();
    map.on('resize', report);
    return () => {
      map.off('resize', report);
    };
    // `bounds` is caller-owned and churns per render; `signature` is its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature, padding, maxZoom, onCamera]);

  return null;
};

const FILL_LAYER_PREFIX = 'tc-area-fill-';

/** One area to draw — inline GeoJSON straight from the candidates response. */
interface InventoryWorkspaceAreaPreviewArea {
  name: string;
  isOrigin?: boolean;
  geometry?: GeoJSON.Geometry | null;
}

interface InventoryWorkspaceAreaPreviewProps {
  /** Every area in play, target first — inline geometry, no separate fetch. */
  areas: InventoryWorkspaceAreaPreviewArea[];
  /** The launched type curve's area name — the caption and the "· target" tag. */
  targetName?: string | null;
  /** Cross-highlight with the context table. */
  activeAreaName?: string | null;
  onAreaClick?: (areaName: string) => void;
  /** Areas toggled off from the legend. */
  hiddenAreaNames?: string[];
  onToggleAreaVisibility?: (areaName: string) => void;
  /**
   * Per-area hover-tooltip text, keyed by area name (e.g.
   * `"proj96_A — 41% overlap · in context · selected"`). Built by the modal,
   * which owns the overlap / in-context / selected context. No entry ⇒ no
   * tooltip for that area.
   */
  areaTooltips?: Map<string, string>;
  /**
   * Extra map content rendered inside the GL canvas, below the area layers —
   * react-map-gl `Source`/`Layer` children compose here. The Eval TC Manager
   * uses this to draw the package's LTD slots over the areas; the launch and
   * Add-or-Remove modals pass nothing and render exactly as before.
   */
  children?: ReactNode;
  /**
   * What the camera frames instead of the areas — the Eval TC Manager passes the selected row's
   * SLOT extent. Null (and every other caller) keeps the union-of-areas framing below.
   *
   * Framing the slots rather than their area is the point of the prop: a Type Curve Area is a
   * large region and a row's places can sit in one corner of it, so fitting the area leaves them
   * too small to see — which defeats clicking the row. It also earns a looser `maxZoom` than the
   * area fit, because the LTD tile only serves sticks (rather than a single surface point) past
   * its own detail zoom.
   */
  fitBounds?: BBox | null;
}

/**
 * Read-only map of the type curve areas this workspace draws from.
 *
 * Ported from the mock's TypeCurveAreaPreview. Each area is two layers: a fill
 * that is the click target, and an outline rendered non-interactive so clicks
 * fall through to the fills below it. Selecting an area highlights its rows in
 * the context table, and the legend toggles each area's visibility.
 *
 * The candidate response carries every area's geometry inline (target first),
 * so the map draws from the response the modal already fetched — no per-area
 * geometry round-trip.
 *
 * @param {InventoryWorkspaceAreaPreviewProps} props - Areas, selection and visibility state.
 * @return {JSX.Element | null} The preview map with its legend.
 */
export const InventoryWorkspaceAreaPreview = ({
  areas,
  targetName,
  activeAreaName,
  onAreaClick,
  hiddenAreaNames = [],
  onToggleAreaVisibility,
  areaTooltips,
  children,
  fitBounds: fitBoundsOverride = null,
}: InventoryWorkspaceAreaPreviewProps) => {
  // The index fixes each area's colour slot; areas arrive target first.
  const drawn = useMemo(
    () =>
      areas
        .map((area, index) => {
          return area.geometry
            ? { name: area.name, index, geometry: area.geometry }
            : null;
        })
        .filter(
          (
            a
          ): a is { name: string; index: number; geometry: GeoJSON.Geometry } =>
            Boolean(a)
        ),
    [areas]
  );

  const visible = drawn.filter((area) => !hiddenAreaNames.includes(area.name));

  // Only the fills are interactive; the outlines sit above them and would
  // otherwise swallow the click.
  const interactiveLayerIds = useMemo(
    () => visible.map((area) => `${FILL_LAYER_PREFIX}${area.index}`),
    [visible]
  );

  // The camera that frames every area, reported by ReportFitCamera and handed
  // straight back to `Map` as its camera props. Held as state, not derived, so
  // `Map`'s own jump has somewhere correct to jump to.
  const [camera, setCamera] = useState<Camera | null>(null);
  const handleCamera = useCallback((next: Camera) => {
    setCamera((prev) => {
      const unchanged =
        prev !== null &&
        Math.abs(prev.latitude - next.latitude) < 1e-9 &&
        Math.abs(prev.longitude - next.longitude) < 1e-9 &&
        Math.abs(prev.zoom - next.zoom) < 1e-6;
      return unchanged ? prev : next;
    });
  }, []);

  // Measuring or dropping coordinate pins owns the click, and sets its own cursor.
  const clicksTaken = useAtomValue(mapClicksTakenAtom);

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (clicksTaken) return;
      const layerId = event.features?.[0]?.layer?.id;
      if (!layerId?.startsWith(FILL_LAYER_PREFIX)) return;
      const index = Number(layerId.slice(FILL_LAYER_PREFIX.length));
      const area = drawn.find((a) => a.index === index);
      if (area) onAreaClick?.(area.name);
    },
    [clicksTaken, drawn, onAreaClick]
  );

  // Mouse-following tooltip: react-map-gl populates `features` from the
  // interactive fill layers on move, so the hovered area is the same
  // layer-id → index → name mapping the click handler uses. Positioning the
  // Popup at the cursor's lngLat on every move makes it follow the pointer.
  const [hover, setHover] = useState<{
    longitude: number;
    latitude: number;
    label: string;
  } | null>(null);

  const handleMapMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const layerId = event.features?.[0]?.layer?.id;
      const index = layerId?.startsWith(FILL_LAYER_PREFIX)
        ? Number(layerId.slice(FILL_LAYER_PREFIX.length))
        : null;
      const area =
        index === null ? undefined : drawn.find((a) => a.index === index);
      const label = area ? areaTooltips?.get(area.name) : undefined;
      if (!label) {
        setHover((prev) => (prev === null ? prev : null));
        return;
      }
      setHover({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        label,
      });
    },
    [drawn, areaTooltips]
  );

  if (drawn.length === 0) return null;

  const center = findCenter(drawn[0].geometry);

  // Frame every area rather than sitting at the map's default zoom, which lands
  // somewhere inside a single section. Mirrors the mock's FitToOutlines: the
  // camera fits the union of the outlines.
  //
  // Deliberately `drawn`, not `visible`: keying this on visibility re-framed the
  // camera on every legend toggle — hide a couple and it zoomed hard onto
  // whatever was left, and hiding all of them zoomed back out to the full set.
  // The legend controls what is drawn, not where the camera sits, so the fit
  // signature has to stay constant across toggles.
  const areaBounds = drawn.map((area) => turfBbox(area.geometry) as BBox);
  const areaFitBounds = unionBounds(areaBounds);
  const fitBounds = fitBoundsOverride
    ? padForFraming(fitBoundsOverride)
    : areaFitBounds;
  // A single stick is a LINE: its box has zero width or height, and `cameraForBounds` answers a
  // zoom in the twenties for one. The override's ceiling is high enough to clear the LTD tile's
  // detail zoom (sticks rather than surface points) and no higher.
  const fitMaxZoom = fitBoundsOverride ? OVERRIDE_MAX_ZOOM : AREA_MAX_ZOOM;

  // The mock's .fillClickable — Mapbox has no CSS hook for a layer, so the
  // canvas cursor is swapped as the pointer enters and leaves the fills.
  const setCursor = (event: MapLayerMouseEvent, cursor: string) => {
    const canvas = event.target?.getCanvas?.();
    if (canvas && !clicksTaken) canvas.style.cursor = cursor;
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <span className={style.label}>Type Curve Area</span>
        {targetName && <span className={style.areaName}>{targetName}</span>}
      </div>
      {/* `showControls` adds the zoom in/out buttons, and is also what makes
          `Map` apply its interaction settings at all — without it the
          `scrollZoom: false` in the config above never reaches Mapbox and the
          preview swallows the wheel from the modal body. The layers, pricing
          and measure controls have no prop to gate them, so they stay. */}
      <PreviewMap
        latitude={camera?.latitude ?? center.latitude}
        longitude={camera?.longitude ?? center.longitude}
        zoom={camera?.zoom}
        className={style.map}
        mapConfigAtom={previewMapConfigAtom}
        interactiveLayerIds={interactiveLayerIds}
        showControls
        onClick={handleMapClick}
        onMouseMove={handleMapMove}
        onMouseEnter={(e) => setCursor(e, 'pointer')}
        onMouseLeave={(e) => {
          setCursor(e, '');
          setHover(null);
        }}
      >
        <KeepMapSized />
        {hover && (
          <Popup
            longitude={hover.longitude}
            latitude={hover.latitude}
            closeButton={false}
            closeOnClick={false}
            offset={12}
            className={mapStyles.popup}
          >
            {hover.label}
          </Popup>
        )}
        {/* `maxZoom` caps how far in the camera may go when the box is small — framing a single
            area alone otherwise opens the preview tight on one polygon. A caller-supplied box
            (the Eval TC Manager's slots) is small ON PURPOSE and gets the looser ceiling. */}
        {fitBounds && (
          <ReportFitCamera
            bounds={fitBounds}
            padding={48}
            maxZoom={fitMaxZoom}
            onCamera={handleCamera}
          />
        )}

        {visible.map((area) => {
          const isActive = area.name === activeAreaName;
          const color = colorForArea(area.index);

          return (
            <Source
              key={area.name}
              id={`tc-area-${area.index}`}
              type="geojson"
              data={area.geometry}
            >
              {/* Fill — the click target. */}
              <Layer
                id={`${FILL_LAYER_PREFIX}${area.index}`}
                type="fill"
                paint={{
                  'fill-color': isActive ? ACTIVE_COLOR : color,
                  'fill-opacity': isActive
                    ? FILL_OPACITY_ACTIVE
                    : FILL_OPACITY_AREA,
                }}
              />
              {/* Outline — never interactive, so clicks reach the fill. */}
              <Layer
                id={`tc-area-line-${area.index}`}
                type="line"
                paint={{
                  'line-color': color,
                  'line-width': STROKE_WIDTH,
                  'line-opacity': 1,
                }}
              />
            </Source>
          );
        })}
        {/* Caller-supplied overlays (see the `children` prop) — after the area
            layers so they render above the fills. */}
        {children}
      </PreviewMap>

      {/* The legend entry IS the visibility toggle — there is no separate
          show/hide control. Selecting an area happens on the map or from the
          table's area tag. Hidden entries strike through, as in the mock. */}
      <div className={style.legend}>
        <span className={style.legendItem}>
          <span className={`${style.swatch} ${style.swatchActive}`} />
          Selected row
        </span>
        {drawn.map((area) => {
          const hidden = hiddenAreaNames.includes(area.name);
          return (
            <button
              key={area.name}
              type="button"
              className={`${style.legendItem} ${style.legendToggle} ${
                hidden ? style.legendHidden : ''
              }`}
              aria-pressed={!hidden}
              title={`${hidden ? 'Show' : 'Hide'} ${area.name} on the map`}
              onClick={() => onToggleAreaVisibility?.(area.name)}
            >
              <span
                className={`${style.swatch} ${style.swatchArea}`}
                style={{ borderTopColor: colorForArea(area.index) }}
              />
              {area.name}
              {area.name === targetName ? ' · target' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};
